import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";

import { CustomerBankInfo } from "store/actions/Payment/GetCustomerBankInfo";
import { getCustomerDetails } from "selectors/customer";

import { getValue } from "components/Screens/CommonUtils";
import AllowCreditCard from "components/Screens/Customers/CustomerDetials/CustomerProfile/AllowCreditCard";

import { baseUrl } from "util/APIBaseUrl";

import { RiBankLine } from "react-icons/ri";

import "./BankInfo.scss";

function BankInfo() {
  const dispatch = useDispatch();
  const params = useParams();

  const bankAccountsDetails = useSelector((state) => state.GetCustomerBankInfo.bankAccountInfo);
  const customerDetails = useSelector(getCustomerDetails);
  const { creditCardSettings, customerId } = customerDetails;
  const allowCreditCard = creditCardSettings?.allowCreditCard || false;
  const [allowCreditCardMethod, setAllowCreditCardMethod] = useState(allowCreditCard);

  const isCreditCard = useMemo(() => {
    if (bankAccountsDetails?.paymentMethodType === "card") return true;
    return false;
  }, [bankAccountsDetails]);

  const isManualPayment = useMemo(() => {
    const type = bankAccountsDetails?.paymentMethodType;
    return type === "CHEQUE" || type === "WIRE_TRANSFER";
  }, [bankAccountsDetails]);

  const paymentMethodLabel = useMemo(() => {
    const labels = { CHEQUE: "Cheque", WIRE_TRANSFER: "Wire Transfer", CREDIT_CARD: "Credit Card", ACH: "ACH" };
    return labels[bankAccountsDetails?.paymentMethodType] || bankAccountsDetails?.paymentMethodType?.toUpperCase();
  }, [bankAccountsDetails]);

  // PRD-2145: a real stored instrument (ACH/card) always has a last four (or a
  // non-manual method type). This is independent of the skipPayment flag — a
  // customer can carry skipPayment = true AND have a saved PM, in which case the
  // PM must still display. (Manual wire/cheque and no-PM customers have neither.)
  const hasStoredPaymentMethod =
    !!bankAccountsDetails?.last4 ||
    ["ACH", "CREDIT_CARD", "card"].includes(bankAccountsDetails?.paymentMethodType);

  // Account-instrument rows (Account Number / Bank Name / Routing) only make sense
  // for a stored instrument — hide them for manual/skip customers instead of
  // rendering blanks like "**** **** **** undefined".
  const showBankInstrumentRows = hasStoredPaymentMethod && !isManualPayment;

  // Show the detailed panel when there's a stored PM or a manual method to display;
  // otherwise fall back to the "No payment method added yet" box (which surfaces
  // the Skip Payment toggle). A skip customer who hasn't paid yet has neither.
  const hasPaymentDetails = hasStoredPaymentMethod || isManualPayment;

  useEffect(() => {
    dispatch(
      CustomerBankInfo({
        request: `${baseUrl}/customer/${params?.id}/bankInfo`,
      })
    );
  }, []);

  useEffect(() => {
    setAllowCreditCardMethod(allowCreditCard);
  }, [customerDetails, allowCreditCard]);

  return (
    <>
      <Grid
        container
        direction="row"
        sx={{
          justifyContent: "space-between",
          alignItems: "center",
          pb: 3.5
        }}>
        <Grid>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              rowGap: 1.25
            }}>
            <RiBankLine color="secondary.main" size="27" />
            <Typography variant="h3" sx={{
              fontSize: "15px"
            }}>
              Payment Information
            </Typography>
          </Box>
        </Grid>
      </Grid>
      {bankAccountsDetails != null && hasPaymentDetails ? (
        <Grid container direction="column" rowSpacing={1.5} size={12}>
          <Grid>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
              }}>
              <Typography
                variant="h6"
                sx={{
                  color: "secondary.alt",
                  fontSize: "13px",
                  marginBottom: "15px"
                }}>
                Payment Method
              </Typography>
              <Typography variant="h5">{getValue(paymentMethodLabel)}</Typography>
            </Box>
          </Grid>
          {showBankInstrumentRows && (
            <Grid>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between"
                }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "secondary.alt",
                    fontSize: "13px"
                  }}>
                  Account Number
                </Typography>
                <Typography variant="h5">
                  {getValue(bankAccountsDetails?.last4 ? `**** **** **** ${bankAccountsDetails?.last4}` : "")}
                </Typography>
              </Box>
            </Grid>
          )}
          <Grid>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
              }}>
              <Typography
                variant="h6"
                sx={{
                  color: "secondary.alt",
                  fontSize: "13px"
                }}>
                Account Name
              </Typography>
              <Typography variant="h5">
                {getValue(bankAccountsDetails?.accountName || bankAccountsDetails?.name)}
              </Typography>
            </Box>
          </Grid>

          {showBankInstrumentRows && (
            <Grid>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between"
                }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "secondary.alt",
                    fontSize: "13px"
                  }}>
                  {isCreditCard ? "Brand" : "Bank Name"}
                </Typography>
                <Typography variant="h5">
                  {isCreditCard ? getValue(bankAccountsDetails?.brand) : getValue(bankAccountsDetails?.bankName)}
                </Typography>
              </Box>
            </Grid>
          )}

          {showBankInstrumentRows && (
            <Grid>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between"
                }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "secondary.alt",
                    fontSize: "13px"
                  }}>
                  {isCreditCard ? "Expiry" : "  Routing Number"}
                </Typography>
                <Typography variant="h5">
                  {isCreditCard ? getValue(bankAccountsDetails?.expiry) : getValue(bankAccountsDetails?.routingNumber)}
                </Typography>
              </Box>
            </Grid>
          )}

          <Grid>
            <Box
              sx={{
                display: "flex",
                alignItems: "start",
                justifyContent: "space-between"
              }}>
              <Typography
                variant="h6"
                sx={{
                  color: "secondary.alt",
                  fontSize: "13px"
                }}>
                Country
              </Typography>
              <Box sx={{ wordBreak: "break-all" }}>
                <Typography variant="h5" sx={{
                  textAlign: "end"
                }}>
                  {getValue(bankAccountsDetails?.country ? bankAccountsDetails?.country : "")}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
              }}>
              <Typography
                variant="h6"
                sx={{
                  color: "secondary.alt",
                  fontSize: "13px"
                }}>
                Currency
              </Typography>
              <Typography variant="h5">{getValue(bankAccountsDetails.currency)}</Typography>
            </Box>
          </Grid>

          <Grid>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
              }}>
              <Typography
                variant="h6"
                sx={{
                  color: "secondary.alt",
                  fontSize: "13px"
                }}>
                Status
              </Typography>
              <Typography variant="h5">{getValue(bankAccountsDetails?.status)}</Typography>
            </Box>
          </Grid>

          <Grid>
            <AllowCreditCard
              customerId={customerId}
              customerDetails={customerDetails}
              allowCreditCard={allowCreditCardMethod}
              setAllowCreditCard={setAllowCreditCardMethod}
              hasPaymentMethod={hasStoredPaymentMethod}
              isAddedCreditCard={customerDetails?.isAddedCreditCard || false}
            />
          </Grid>
        </Grid>
      ) : (
        <>
          <div className="no-payment-method-box">
            <p className="no-payment-method-text">No payment method added yet.</p>
          </div>
          <div>
            <AllowCreditCard
              customerId={customerId}
              customerDetails={customerDetails}
              allowCreditCard={allowCreditCardMethod}
              setAllowCreditCard={setAllowCreditCardMethod}
              hasPaymentMethod={false}
              isAddedCreditCard={customerDetails?.isAddedCreditCard || false}
            />
          </div>
        </>
      )}
    </>
  );
}

export default BankInfo;
